import { Children, isValidElement, ReactNode } from 'react'

/*
 * Helpers for the Assistant's user guide pages (markdown served by the API).
 * Example prompts are written as links to "#prompt" — [Show overdue invoices](#prompt) —
 * and render as "try it" chips that put the text in the composer.
 */

export const PROMPT_HREF = '#prompt'

export const isPromptLink = (href?: string) => href === PROMPT_HREF

/** Plain text of rendered markdown children (link text, heading text). */
export const nodeText = (node: ReactNode): string => {
  if (node == null || typeof node === 'boolean') {
    return ''
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node)
  }
  if (Array.isArray(node)) {
    return node.map(nodeText).join('')
  }
  if (isValidElement(node)) {
    return Children.toArray((node.props as any).children)
      .map(nodeText)
      .join('')
  }
  return ''
}

/** Anchor id for a section heading ("Find cases" → "find-cases"). */
export const headingId = (title: string) =>
  title
    .toLowerCase()
    .replace(/[`*_]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

export interface GuideHeading {
  id: string
  title: string
}

export interface HeadingPosition {
  id: string
  /** Top of the heading within the scrolled content (px). */
  top: number
}

/**
 * The section being read (scroll-spy): the last heading at or above the reading line near the top
 * of the panel; null before the first heading. At the very bottom the last heading wins, since the
 * final sections may never reach the top.
 */
export const activeHeadingId = (
  positions: HeadingPosition[],
  scrollTop: number,
  viewportHeight: number,
  scrollHeight: number,
  readingLine = 56
): string | null => {
  if (positions.length === 0) {
    return null
  }
  if (scrollTop > 0 && scrollTop + viewportHeight >= scrollHeight - 2) {
    return positions[positions.length - 1].id
  }
  let active: string | null = null
  for (const position of positions) {
    if (position.top - scrollTop > readingLine) {
      break
    }
    active = position.id
  }
  return active
}

/** A shareable link to a guide page / section (opens the standalone Assistant page). */
export const guideLink = (origin: string, key: string, section?: string) =>
  `${origin}/view/assistant?guide=${encodeURIComponent(key)}` +
  (section ? `&section=${encodeURIComponent(section)}` : '')

/** The page's sections (## headings) for the navigator. */
export const guideHeadings = (markdown: string): GuideHeading[] =>
  markdown
    .split('\n')
    .map(line => /^##\s+(.+?)\s*$/.exec(line)?.[1])
    .filter((title): title is string => !!title)
    .map(title => {
      const plain = title.replace(/[`*_]/g, '')
      return { id: headingId(plain), title: plain }
    })
